/**
 * drugDatabase.js —— 常用药品参考库
 *
 * 干什么用的：
 *   给「录入核对」提供比对基准 —— 看用户填的剂量、频次是否落在常见范围里。
 *   它**不做医学判断**，只做范围比对，所有结论都必须以药盒说明书和医嘱为准。
 *
 * ⚠️ 口径说明（重要，别在文档或界面里把它说成 AI）：
 *   这里是一份人工整理的规则数据 + 正则，不是模型推理。界面上原来叫「AI 校验」，
 *   容易让人以为背后有大模型在判断，实际不是。要真接大模型得配 API key，属另一件事。
 *
 * 数据来源（2026-09 联网核对）：
 *   1. 腾讯医典药品说明书条目：氨氯地平 / 培哚普利 / 缬沙坦 / 格列美脲 / 格列齐特 /
 *      西格列汀 / 恩格列净 / 速效救心丸 / 复方丹参滴丸 / 通心络胶囊
 *   2. 《冠心病合理用药指南》中成药推荐剂量表（麝香保心丸 / 复方丹参滴丸 / 通心络 等）
 *   3. 美国 JNC7 高血压指南「口服降压药日剂量表」，用于交叉核对降压药区间
 *   4. 其余为该类药品国内说明书的通行常规剂量
 *   5. 2026-09-22 增补：维生素B12片（一日 25~100μg 分次服用，规格 25μg / 50μg
 *      —— 中国医药信息查询平台 / 腾讯医典 说明书条目，因规格跨越大只比频次不比剂量）
 *
 * 另有两张「药名归一」表（ASR_FIXES / BRAND_ALIAS，见文件后半段）：
 * 前者纠语音识别的同音错字，后者把商品名换成通用名。两张表**不参与剂量判断**。
 *
 * 字段说明：
 *   dosageRange —— 单次给药的常见范围（数值区间），单位见 unit；
 *                  null 表示该药剂型/规格差异太大（如浓缩丸与大蜜丸差 8 倍），
 *                  不做剂量比对，只比对频次 —— 宁可不提示，也不要误导。
 *   unit        —— 该药剂量最常用的单位。
 *   commonFreq  —— 常见服用频次，用于提示「这个频次少见」。
 */

export const KNOWN_DRUGS = {
  /* ============ 降压药 ============ */
  '氨氯地平': { dosageRange: [2.5, 10], unit: 'mg', commonFreq: ['每日1次'] },
  '左旋氨氯地平': { dosageRange: [2.5, 5], unit: 'mg', commonFreq: ['每日1次'] },
  '硝苯地平': { dosageRange: [5, 30], unit: 'mg', commonFreq: ['每日1次', '每日3次'] },
  '尼群地平': { dosageRange: [10, 20], unit: 'mg', commonFreq: ['每日2次'] },
  '非洛地平': { dosageRange: [2.5, 10], unit: 'mg', commonFreq: ['每日1次'] },
  '地尔硫卓': { dosageRange: [30, 60], unit: 'mg', commonFreq: ['每日3次'] },
  '缬沙坦': { dosageRange: [80, 160], unit: 'mg', commonFreq: ['每日1次'] },
  '厄贝沙坦': { dosageRange: [150, 300], unit: 'mg', commonFreq: ['每日1次'] },
  '氯沙坦': { dosageRange: [50, 100], unit: 'mg', commonFreq: ['每日1次'] },
  '替米沙坦': { dosageRange: [40, 80], unit: 'mg', commonFreq: ['每日1次'] },
  '坎地沙坦': { dosageRange: [4, 16], unit: 'mg', commonFreq: ['每日1次'] },
  '奥美沙坦': { dosageRange: [20, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '沙库巴曲缬沙坦': { dosageRange: [100, 200], unit: 'mg', commonFreq: ['每日2次'] },
  '培哚普利': { dosageRange: [4, 8], unit: 'mg', commonFreq: ['每日1次'] },
  '依那普利': { dosageRange: [5, 20], unit: 'mg', commonFreq: ['每日1次', '每日2次'] },
  '卡托普利': { dosageRange: [12.5, 50], unit: 'mg', commonFreq: ['每日2次', '每日3次'] },
  '贝那普利': { dosageRange: [10, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '赖诺普利': { dosageRange: [10, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '福辛普利': { dosageRange: [10, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '美托洛尔': { dosageRange: [12.5, 100], unit: 'mg', commonFreq: ['每日1次', '每日2次'] },
  '比索洛尔': { dosageRange: [2.5, 10], unit: 'mg', commonFreq: ['每日1次'] },
  '阿替洛尔': { dosageRange: [6.25, 25], unit: 'mg', commonFreq: ['每日1次', '每日2次'] },
  '普萘洛尔': { dosageRange: [10, 40], unit: 'mg', commonFreq: ['每日2次', '每日3次'] },
  '卡维地洛': { dosageRange: [6.25, 25], unit: 'mg', commonFreq: ['每日2次'] },
  '氢氯噻嗪': { dosageRange: [12.5, 25], unit: 'mg', commonFreq: ['每日1次'] },
  '吲达帕胺': { dosageRange: [1.25, 2.5], unit: 'mg', commonFreq: ['每日1次'] },
  '呋塞米': { dosageRange: [20, 40], unit: 'mg', commonFreq: ['每日1次', '每日2次'] },
  '螺内酯': { dosageRange: [20, 40], unit: 'mg', commonFreq: ['每日1次'] },

  /* ============ 冠心病 / 心绞痛 ============ */
  '单硝酸异山梨酯': { dosageRange: [20, 40], unit: 'mg', commonFreq: ['每日2次'] },
  '硝酸甘油': { dosageRange: [0.3, 0.6], unit: 'mg', commonFreq: ['必要时'] },
  '曲美他嗪': { dosageRange: [20, 40], unit: 'mg', commonFreq: ['每日3次'] },
  '速效救心丸': { dosageRange: [4, 8], unit: '丸', commonFreq: ['每日3次', '必要时'] },
  '复方丹参滴丸': { dosageRange: [10, 10], unit: '丸', commonFreq: ['每日3次'] },
  '复方丹参片': { dosageRange: [3, 3], unit: '片', commonFreq: ['每日3次'] },
  '麝香保心丸': { dosageRange: [1, 2], unit: '丸', commonFreq: ['每日3次'] },
  '通心络胶囊': { dosageRange: [2, 4], unit: '粒', commonFreq: ['每日3次'] },
  '冠心舒通胶囊': { dosageRange: [3, 3], unit: '粒', commonFreq: ['每日3次'] },
  '稳心颗粒': { dosageRange: [1, 1], unit: '袋', commonFreq: ['每日3次'] },
  '银杏叶片': { dosageRange: [1, 2], unit: '片', commonFreq: ['每日3次'] },
  '血塞通片': { dosageRange: [1, 2], unit: '片', commonFreq: ['每日3次'] },
  '辅酶Q10': { dosageRange: [10, 20], unit: 'mg', commonFreq: ['每日3次'] },

  /* ============ 抗血小板 / 抗凝 ============ */
  '阿司匹林': { dosageRange: [75, 500], unit: 'mg', commonFreq: ['每日1次', '每日3次'] },
  '氯吡格雷': { dosageRange: [75, 75], unit: 'mg', commonFreq: ['每日1次'] },
  '替格瑞洛': { dosageRange: [90, 90], unit: 'mg', commonFreq: ['每日2次'] },
  '华法林': { dosageRange: [1, 5], unit: 'mg', commonFreq: ['每日1次'] },
  '利伐沙班': { dosageRange: [10, 20], unit: 'mg', commonFreq: ['每日1次'] },
  '达比加群酯': { dosageRange: [110, 150], unit: 'mg', commonFreq: ['每日2次'] },

  /* ============ 调脂药 ============ */
  '阿托伐他汀': { dosageRange: [10, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '瑞舒伐他汀': { dosageRange: [5, 20], unit: 'mg', commonFreq: ['每日1次'] },
  '辛伐他汀': { dosageRange: [10, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '普伐他汀': { dosageRange: [10, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '氟伐他汀': { dosageRange: [20, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '依折麦布': { dosageRange: [10, 10], unit: 'mg', commonFreq: ['每日1次'] },
  '非诺贝特': { dosageRange: [145, 200], unit: 'mg', commonFreq: ['每日1次'] },

  /* ============ 降糖药 ============ */
  '二甲双胍': { dosageRange: [250, 1000], unit: 'mg', commonFreq: ['每日2次', '每日3次'] },
  '阿卡波糖': { dosageRange: [50, 100], unit: 'mg', commonFreq: ['每日3次'] },
  '格列美脲': { dosageRange: [1, 4], unit: 'mg', commonFreq: ['每日1次'] },
  '格列齐特': { dosageRange: [30, 80], unit: 'mg', commonFreq: ['每日1次', '每日2次'] },
  '格列吡嗪': { dosageRange: [5, 10], unit: 'mg', commonFreq: ['每日1次', '每日2次'] },
  '格列喹酮': { dosageRange: [30, 60], unit: 'mg', commonFreq: ['每日2次', '每日3次'] },
  '瑞格列奈': { dosageRange: [0.5, 2], unit: 'mg', commonFreq: ['每日3次'] },
  '西格列汀': { dosageRange: [100, 100], unit: 'mg', commonFreq: ['每日1次'] },
  '维格列汀': { dosageRange: [50, 50], unit: 'mg', commonFreq: ['每日2次'] },
  '沙格列汀': { dosageRange: [5, 5], unit: 'mg', commonFreq: ['每日1次'] },
  '达格列净': { dosageRange: [10, 10], unit: 'mg', commonFreq: ['每日1次'] },
  '恩格列净': { dosageRange: [10, 25], unit: 'mg', commonFreq: ['每日1次'] },

  /* ============ 胃药 / 消化 ============ */
  '奥美拉唑': { dosageRange: [20, 20], unit: 'mg', commonFreq: ['每日1次', '每日2次'] },
  '泮托拉唑': { dosageRange: [40, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '雷贝拉唑': { dosageRange: [10, 20], unit: 'mg', commonFreq: ['每日1次'] },
  '埃索美拉唑': { dosageRange: [20, 40], unit: 'mg', commonFreq: ['每日1次'] },
  '雷尼替丁': { dosageRange: [150, 150], unit: 'mg', commonFreq: ['每日2次'] },
  '法莫替丁': { dosageRange: [20, 20], unit: 'mg', commonFreq: ['每日2次'] },
  '铝碳酸镁': { dosageRange: [500, 1000], unit: 'mg', commonFreq: ['每日3次'] },
  '复方氢氧化铝': { dosageRange: [2, 3], unit: '片', commonFreq: ['每日3次'] },
  '多潘立酮': { dosageRange: [10, 10], unit: 'mg', commonFreq: ['每日3次'] },
  '莫沙必利': { dosageRange: [5, 5], unit: 'mg', commonFreq: ['每日3次'] },
  '蒙脱石散': { dosageRange: [1, 1], unit: '袋', commonFreq: ['每日3次'] },
  '乳果糖': { dosageRange: [15, 30], unit: 'ml', commonFreq: ['每日1次'] },
  '双歧杆菌': { dosageRange: [1, 2], unit: '袋', commonFreq: ['每日2次', '每日3次'] },

  /* ============ 解热镇痛 / 感冒 / 呼吸 ============ */
  '对乙酰氨基酚': { dosageRange: [300, 600], unit: 'mg', commonFreq: ['每日3次', '每日4次'] },
  '布洛芬': { dosageRange: [200, 400], unit: 'mg', commonFreq: ['每日2次', '每日3次'] },
  '双氯芬酸钠': { dosageRange: [25, 50], unit: 'mg', commonFreq: ['每日2次', '每日3次'] },
  '氨溴索': { dosageRange: [30, 30], unit: 'mg', commonFreq: ['每日3次'] },
  '复方甘草片': { dosageRange: [1, 2], unit: '片', commonFreq: ['每日3次'] },
  '连花清瘟胶囊': { dosageRange: [4, 4], unit: '粒', commonFreq: ['每日3次'] },
  '感冒灵颗粒': { dosageRange: [1, 1], unit: '袋', commonFreq: ['每日3次'] },
  '板蓝根颗粒': { dosageRange: [1, 2], unit: '袋', commonFreq: ['每日3次'] },
  '蓝芩口服液': { dosageRange: [1, 1], unit: '支', commonFreq: ['每日3次'] },
  '孟鲁司特': { dosageRange: [10, 10], unit: 'mg', commonFreq: ['每日1次'] },
  '氯雷他定': { dosageRange: [10, 10], unit: 'mg', commonFreq: ['每日1次'] },
  '西替利嗪': { dosageRange: [10, 10], unit: 'mg', commonFreq: ['每日1次'] },
  '氨茶碱': { dosageRange: [100, 200], unit: 'mg', commonFreq: ['每日2次', '每日3次'] },

  /* ============ 抗感染 ============ */
  '阿莫西林': { dosageRange: [250, 500], unit: 'mg', commonFreq: ['每日3次', '每日4次'] },
  '阿莫西林克拉维酸钾': { dosageRange: [457, 914], unit: 'mg', commonFreq: ['每日2次'] },
  '头孢氨苄': { dosageRange: [250, 500], unit: 'mg', commonFreq: ['每日3次', '每日4次'] },
  '头孢克肟': { dosageRange: [50, 100], unit: 'mg', commonFreq: ['每日2次'] },
  '头孢呋辛酯': { dosageRange: [250, 500], unit: 'mg', commonFreq: ['每日2次'] },
  '阿奇霉素': { dosageRange: [250, 500], unit: 'mg', commonFreq: ['每日1次'] },
  '左氧氟沙星': { dosageRange: [200, 500], unit: 'mg', commonFreq: ['每日1次'] },
  '诺氟沙星': { dosageRange: [300, 400], unit: 'mg', commonFreq: ['每日2次'] },
  '甲硝唑': { dosageRange: [200, 400], unit: 'mg', commonFreq: ['每日3次'] },

  /* ============ 神经 / 骨科 / 补钙 ============ */
  '甲钴胺': { dosageRange: [0.5, 0.5], unit: 'mg', commonFreq: ['每日3次'] },
  '维生素B1': { dosageRange: [10, 10], unit: 'mg', commonFreq: ['每日3次'] },
  // 维生素B12 片有两种规格（25μg / 50μg），说明书按「一日总量」给药：
  // 「口服。一日 25~100μg(1~4片)或隔日 50~200μg(2~8片)，分次服用或遵医嘱」。
  // 规格不同，折算成「片」的口径就不同 —— 与本库其他「规格差异大」的药一致，
  // 只比频次、不比剂量，避免拿错规格的区间去吓用户。
  '维生素B12': { dosageRange: null, unit: '片', commonFreq: ['每日1次', '每日2次', '每日3次'] },
  '碳酸钙D3': { dosageRange: [1, 1], unit: '片', commonFreq: ['每日1次', '每日2次'] },
  '阿仑膦酸钠': { dosageRange: [70, 70], unit: 'mg', commonFreq: ['每周1次'] },
  '叶酸': { dosageRange: [0.4, 5], unit: 'mg', commonFreq: ['每日1次'] },
  '骨化三醇': { dosageRange: null, unit: 'μg', commonFreq: ['每日1次'] },
  '左甲状腺素钠': { dosageRange: [0.0125, 0.2], unit: 'mg', commonFreq: ['每日1次'] },

  /* ============ 尿酸 / 泌尿 / 其他 ============ */
  '别嘌醇': { dosageRange: [50, 100], unit: 'mg', commonFreq: ['每日1次', '每日2次'] },
  '非布司他': { dosageRange: [40, 80], unit: 'mg', commonFreq: ['每日1次'] },
  '苯溴马隆': { dosageRange: [50, 50], unit: 'mg', commonFreq: ['每日1次'] },
  '碳酸氢钠': { dosageRange: [500, 1000], unit: 'mg', commonFreq: ['每日3次'] },
  '坦索罗辛': { dosageRange: [0.2, 0.2], unit: 'mg', commonFreq: ['每日1次'] },
  '非那雄胺': { dosageRange: [5, 5], unit: 'mg', commonFreq: ['每日1次'] },
  '六味地黄丸': { dosageRange: null, unit: '丸', commonFreq: ['每日2次', '每日3次'] },
  '甲巯咪唑': { dosageRange: [5, 10], unit: 'mg', commonFreq: ['每日1次', '每日2次'] },
  '氯化钾缓释片': { dosageRange: [500, 1000], unit: 'mg', commonFreq: ['每日2次', '每日3次'] },
};

/** 药品名按长度降序匹配，避免「左旋氨氯地平」被「氨氯地平」抢先命中 */
export const DRUG_NAMES = Object.keys(KNOWN_DRUGS).sort((a, b) => b.length - a.length);

/**
 * 按「子串包含」匹配药品，让「阿司匹林肠溶片」能命中「阿司匹林」。
 * 命中返回 { key, info }，未命中返回 null。
 */
export function matchKnownDrug(name) {
  if (!name) return null;
  const compact = String(name).replace(/\s+/g, '');
  for (const key of DRUG_NAMES) {
    const idx = compact.indexOf(key);
    if (idx < 0) continue;

    // 库里最长的名字也可能只是别人名字的一段前缀：
    // 文本里的「维生素B12」含有库中的「维生素B1」，直接子串命中会把 B12 认成 B1。
    // 认错药比认不出药危险得多，所以这里要挡一道。
    // 判据：这条库名本身以字母/数字收尾，且正文紧接着还是字母/数字 ⟹ 真名更长，跳过。
    // 「阿司匹林100mg」不会触发：库名尾字是汉字，「100」属于后面的剂量。
    const last = key[key.length - 1];
    const after = compact[idx + key.length];
    if (after && /[0-9A-Za-z]/.test(last) && /[0-9A-Za-z]/.test(after)) continue;

    return { key, info: KNOWN_DRUGS[key] };
  }
  return null;
}

/**
 * 中文药品的剂型后缀，按长度降序。
 *
 * 为什么需要它：药名识别原来完全依赖上面的名单，名单外的药（尤其医院自制制剂、
 * 中成药、新药）一律识别不出名字，用户感知就是「语音说了半天，药名那栏还是空的」。
 * 有这张后缀表，就能从「医生让我吃蓝芩口服液」里把「蓝芩口服液」抠出来。
 *
 * 单字的「丸 / 片 / 散 / 粉」保留（都是明确剂型，老人常说的「三七粉」就靠它认出来）；
 * 单字的「剂 / 液 / 膏」没收 —— 太宽泛，会把「药剂科」「口服液瓶」这类也算成药名。
 * 多字的「合剂 / 混悬液 / 口服溶液」是收的：它们本身就是剂型名，不会误伤。
 */
export const DOSE_FORMS = [
  '缓释胶囊', '缓释片', '控释片', '肠溶胶囊', '肠溶片', '分散片', '咀嚼片', '泡腾片',
  '薄膜衣片', '口崩片', '口含片', '含片', '滴丸', '浓缩丸', '软胶囊', '胶囊', '颗粒',
  '口服液', '口服溶液', '糖浆', '混悬液', '干混悬剂', '合剂', '水丸', '蜜丸',
  '滴眼液', '滴鼻液', '喷雾剂', '气雾剂', '吸入剂', '软膏', '乳膏', '贴剂', '栓剂',
  '散剂', '散', '丸', '片', '粉',
].sort((a, b) => b.length - a.length);

/**
 * 药名前面常见的废话，需要反复剥掉：
 * 「医生让我吃感冒灵颗粒」→ 剥掉「医生」「让」「我」「吃」→「感冒灵颗粒」。
 * 用「反复剥离」而不是一次 replace，是因为前缀可能叠好几层。
 */
const NAME_PREFIX_NOISE =
  /^(我|你|他|她|它|的|了|是|叫|吃|喝|服|用|服用|吃的|喝的|用的|吃的是|服用的是|每天|每日|一天|一次|每次|早上|早晨|上午|中午|下午|晚上|睡前|饭前|饭后|空腹|医生|大夫|医院|药店|让|说|开|开了|给我|这个|那个|这|那|种|一种|药|名字|叫做|还有|再|要|想|记得|开始|继续)+/;

/**
 * 候选药名里一旦出现这些，说明抠到频次/时间描述里去了，根本不是药名。
 * 典型：「每天三次，每次两片」如果只看「片」字往前抠，会抠出「三次，每次两」。
 *
 * 「次 / 数 / 每 / 各」这几个字在中药通用名里基本不出现，拿来当哨兵很安全。
 * 注意**别把「天 / 日」也放进来** —— 天王补心丸、天麻钩藤饮这些名字里就有「天」。
 */
const NOT_IN_DRUG_NAME = /[，。、；：！？,.;:!?（）()\s]|[0-9]|[次数每各]/;

/* ------------------------------------------------------------------ *
 * 药名归一：语音错字纠正 + 商品名转通用名
 *
 * 这两张表只解决一件事 —— **让药名能被认出来**，不参与任何剂量判断，
 * 也不改变剂量 / 频次 / 时间的解析结果。
 * ------------------------------------------------------------------ */

/**
 * 语音识别最容易听错的字，先纠正再拿去匹配药名。
 *
 * 为什么必须要有：一个同音字对不上，整句就认不出药名 ——
 * 说「二甲双胍」被识别成「二甲双瓜」、说「阿托伐他汀」被识别成「阿托伐他丁」，
 * 这是「语音说了半天，药名那栏还是空的」最主要的成因。
 *
 * 说明：这里只做同音 / 近音的写法归一，属于识别层的兜底，不是医学数据。
 * 「阿斯匹林 / 阿司匹灵」严格说不是错字，是药店常见的另一种写法，一并归一到通用名。
 */
export const ASR_FIXES = [
  ['阿斯匹林', '阿司匹林'], ['阿司匹灵', '阿司匹林'], ['阿斯匹灵', '阿司匹林'],
  ['双瓜', '双胍'], ['二甲双孤', '二甲双胍'], ['二甲双呱', '二甲双胍'],
  ['他丁', '他汀'],
  ['头胞', '头孢'], ['头苞', '头孢'],
  ['蒲地兰', '蒲地蓝'],
  ['氨路地平', '氨氯地平'], ['氨禄地平', '氨氯地平'],
  ['硝本地平', '硝苯地平'], ['消本地平', '硝苯地平'], ['消苯地平', '硝苯地平'],
  ['缬沙坛', '缬沙坦'], ['结沙坦', '缬沙坦'],
  ['厄贝沙坛', '厄贝沙坦'],
  ['螺内脂', '螺内酯'],
  ['奥美拉坐', '奥美拉唑'],
  ['阿莫西淋', '阿莫西林'],
  ['阿奇酶素', '阿奇霉素'], ['阿齐霉素', '阿奇霉素'],
  ['格列美尿', '格列美脲'],
  ['夫塞米', '呋塞米'],
  ['笨磺酸', '苯磺酸'],
  ['美托洛儿', '美托洛尔'],
  ['布罗芬', '布洛芬'],
  ['甲古胺', '甲钴胺'],
  ['达格列静', '达格列净'],
  ['阿卡波唐', '阿卡波糖'],
  ['氯吡格蕾', '氯吡格雷'],
  ['单参', '丹参'],
  ['三七分', '三七粉'],
];

/**
 * 常见商品名 → 通用名。
 *
 * 为什么必须要有：老人记的是药盒上印的商品名（「络活喜」「拜新同」「格华止」），
 * 这类名字既没有剂型后缀、又不在通用名参考库里 —— 原来一律识别不出药名。
 * 归一之后不但名字能填上，还能顺带拿到通用名的剂量区间做核对。
 *
 * 收录范围：慢病门诊 / 国家集采最常见的原研与首仿商品名，**不追求穷举**。
 * 依据：各厂家说明书与药品批准文号公布的通用名（2026-09 核对），
 * 括号里是该商品名的持有厂家，便于日后核对与增删。
 */
export const BRAND_ALIAS = {
  // 抗血小板 / 调脂
  '拜阿司匹灵': '阿司匹林', // 拜耳
  '拜阿司匹林': '阿司匹林',
  '波立维': '氯吡格雷', '泰嘉': '氯吡格雷', // 赛诺菲 / 信立泰
  '立普妥': '阿托伐他汀', '阿乐': '阿托伐他汀', // 辉瑞 / 嘉林
  '可定': '瑞舒伐他汀', // 阿斯利康
  '舒降之': '辛伐他汀', // 默沙东
  '普拉固': '普伐他汀', // 施贵宝
  '力平之': '非诺贝特', // 雅培

  // 降压
  '拜新同': '硝苯地平', '心痛定': '硝苯地平', // 拜耳 / 常用名
  '络活喜': '氨氯地平', // 辉瑞
  '施慧达': '左旋氨氯地平', // 施慧达药业
  '代文': '缬沙坦', // 诺华
  '安博维': '厄贝沙坦', // 赛诺菲
  '科素亚': '氯沙坦', // 默沙东
  '必洛斯': '坎地沙坦', // 武田
  '美卡素': '替米沙坦', // 勃林格殷格翰
  '倍他乐克': '美托洛尔', // 阿斯利康
  '波依定': '非洛地平', // 阿斯利康
  '合贝爽': '地尔硫卓', // 田边
  '蒙诺': '福辛普利', // 施贵宝
  '洛汀新': '贝那普利', // 诺华
  '开博通': '卡托普利', // 施贵宝
  '雅施达': '培哚普利', // 施维雅
  '悦宁定': '依那普利', // 默沙东

  // 冠心病
  '欣康': '单硝酸异山梨酯', '依姆多': '单硝酸异山梨酯', // 鲁南 / 默沙东

  // 降糖
  '格华止': '二甲双胍', // 默克
  '拜糖平': '阿卡波糖', '拜唐苹': '阿卡波糖', // 拜耳
  '亚莫利': '格列美脲', // 赛诺菲
  '达美康': '格列齐特', // 施维雅
  '捷诺维': '西格列汀', // 默沙东
  '安达唐': '达格列净', // 阿斯利康

  // 消化 / 解热镇痛 / 抗过敏 / 抗感染 / 其他
  '洛赛克': '奥美拉唑', '耐信': '埃索美拉唑', // 阿斯利康
  '吗丁啉': '多潘立酮', // 西安杨森
  '达喜': '铝碳酸镁', // 拜耳
  '泰诺林': '对乙酰氨基酚', // 强生
  '芬必得': '布洛芬', // 中美史克
  '开瑞坦': '氯雷他定', // 拜耳
  '希舒美': '阿奇霉素', // 辉瑞
  '优甲乐': '左甲状腺素钠', // 默克
  '立加利仙': '苯溴马隆', // 德国 Heumann
};

/** 商品名按长度降序替换，避免「拜阿司匹灵」被更短的别名抢先切碎 */
const BRAND_NAMES = Object.keys(BRAND_ALIAS).sort((a, b) => b.length - a.length);

/**
 * 把「语音错字 + 商品名」先归一成通用名的写法。
 * 只动药名相关的字，句子其余部分原样保留（剂量、频次、时间还要靠它解析）。
 */
function normalizeAliases(text) {
  let s = String(text || '');
  for (const [wrong, right] of ASR_FIXES) {
    if (s.includes(wrong)) s = s.split(wrong).join(right);
  }
  for (const brand of BRAND_NAMES) {
    if (s.includes(brand)) s = s.split(brand).join(BRAND_ALIAS[brand]);
  }
  return s;
}

/**
 * 从一段话里认出药品名。
 *
 * 两级策略：
 *   1) 先查参考库（最可靠），命中就返回标准名 —— 顺便能拿到剂量区间做核对；
 *   2) 库外再按剂型后缀从文本里抠，抠出来的名字照样能填进表单，
 *      只是没有参考区间、不做剂量比对。
 *
 * 返回 { name, info, fromLibrary }，认不出返回 null。
 */
export function extractDrugName(text) {
  const compact = normalizeAliases(text).replace(/\s+/g, '');
  if (!compact) return null;

  const hit = matchKnownDrug(compact);
  if (hit) return { name: hit.key, info: hit.info, fromLibrary: true };

  for (const form of DOSE_FORMS) {
    let idx = compact.indexOf(form);
    while (idx > 0) {
      // 从剂型词往前最多取 10 个字当候选，再反复剥掉前缀废话
      let cand = compact.slice(0, idx).slice(-10);
      let prev;
      do {
        prev = cand;
        cand = cand.replace(NAME_PREFIX_NOISE, '');
      } while (cand !== prev);

      // 药名至少 2 个字。只剩一个字时基本是把「每次两片」的量词当成了药名；
      // 候选里混进标点/数字/频次词同样说明抠错了位置，继续往后找下一个剂型词
      if (cand.length >= 2 && !NOT_IN_DRUG_NAME.test(cand)) {
        return { name: cand + form, info: null, fromLibrary: false };
      }
      idx = compact.indexOf(form, idx + form.length);
    }
  }

  return null;
}
